import React, { memo, useMemo } from "react";
import { Highlight, HStack, Skeleton, Text, VStack } from "@chakra-ui/react";
import colors from "app/colors";
import { FileFragment, MessageFragment, StatusType } from "gql/graphql";
import { SyncIcon, TranslateIcon } from "modules/icons";
import { useAuth } from "modules/providers/auth-provider";
import AudioFilePlayer from "../_shared/audio-file-player";
import { useFile } from "modules/providers/file-provider";
import useQueryString from "modules/hooks/use-query-string";

interface IProps {
  message: MessageFragment;
  file: FileFragment;
  messageThreadLocale?: string | null;
}

const MessageAudioFile = ({ message, file, messageThreadLocale }: IProps) => {
  const { me } = useAuth();
  const { localFilesById } = useFile();
  const { qsMessageHighlights } = useQueryString();

  const isViewingTranslated = useMemo(
    () =>
      !!messageThreadLocale &&
      messageThreadLocale !== file.voiceNoteLocale &&
      me?.id !== file?.userId,
    [file?.userId, file.voiceNoteLocale, messageThreadLocale, me?.id],
  );

  const hasReactions = useMemo(
    () =>
      !!message.reactionList?.some((reaction) => reaction.fileId === file.id),
    [file.id, message.reactionList],
  );

  //currently only for audio files for messages in the process of being sent
  const localFile = localFilesById[file.id];

  return (
    <VStack
      w="full"
      spacing={0}
      px={2}
      pt={1}
      pb={hasReactions ? 8 : 1}
      alignItems="flex-start"
    >
      {file.isVoiceNote && (
        <VStack w="full" alignItems="flex-start" fontWeight={700}>
          <Text color={colors.blackSecondary} fontSize={14}>
            Voice Transcription
          </Text>
          {(file.voiceNoteStatus === StatusType.Created ||
            file.voiceNoteStatus === StatusType.Started) && (
            <VStack w="full" alignItems="flex-start" spacing={1}>
              <Skeleton w="40%" h="16px" color={colors.gray30} />
              <Skeleton w="full" h="16px" color={colors.gray30} />
              <Skeleton w="30%" h="16px" color={colors.gray30} />
              <HStack w="full">
                <SyncIcon boxSize={4} />
                <Text color={colors.gray50} fontSize={12} fontWeight={600}>
                  Transcribing audio...
                </Text>
              </HStack>
            </VStack>
          )}
          {file.voiceNoteStatus === StatusType.Complete &&
            !isViewingTranslated &&
            !!file.voiceNoteText && (
              <Text
                color={colors.blackSecondary}
                fontStyle="italic"
                fontSize={14}
                fontWeight={400}
              >
                <Highlight
                  query={qsMessageHighlights}
                  styles={{
                    bg: colors.yellow,
                    margin: "0 !important",
                  }}
                >
                  {file.voiceNoteText}
                </Highlight>
              </Text>
            )}
          {file.voiceNoteStatus === StatusType.Complete &&
            isViewingTranslated &&
            !!file.voiceNoteLocalizedText && (
              <Text
                color={colors.blackSecondary}
                fontStyle="italic"
                fontSize={16}
                fontWeight={400}
              >
                <TranslateIcon boxSize={5} fill={colors.grayMain} pr={1} />
                <Highlight
                  query={qsMessageHighlights}
                  styles={{
                    bg: colors.yellow,
                    margin: "0 !important",
                  }}
                >
                  {file.voiceNoteLocalizedText}
                </Highlight>
              </Text>
            )}
          {(!file.voiceNoteStatus ||
            file.voiceNoteStatus === StatusType.Error) && (
            <Text
              color={colors.blackSecondary}
              fontStyle="italic"
              fontSize={16}
              fontWeight={400}
            >
              Unable to transcribe audio
            </Text>
          )}
        </VStack>
      )}
      <AudioFilePlayer
        audioData={file.cloudFrontUrl ?? localFile}
        fileName={file.name}
      />
    </VStack>
  );
};

export default memo(MessageAudioFile);
